import React from 'react';
// Mui
import { styled } from '@mui/material';
import MenuItem, { MenuItemProps } from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
// Shared
import { CustomBadge } from 'src/app/shared/Badge';
// Interface
import { Notification as NotificationObject } from 'src/app/models/AppNotification';
// Icons
import ClearIcon from '@mui/icons-material/Clear';
import { NotificationType } from 'src/app/models/notificationTypes';
import { useAppSelector } from 'src/app/store/stateTypeHook';
import { useNavigate } from 'react-router-dom';

interface INotificationItem extends MenuItemProps {
    bookingtype?: 'abc' | 'ccd';
}

interface IProps {
    notification: NotificationObject;
    notificationResolve: (id: string) => void;
    notificationClose: (id: string) => void;
    gotoBooking: (bookingRef: string, bookingJourneyRef: string) => void;
    handleActionClose?: () => void;
    key: any;
}

const getWindowType = {
    Collection: {
        title: 'Collection Window',
        message:
            'The driver for booking $bookingRef is at risk of missing the collection window. Please contact the customer.',
    },
    Injection: {
        title: 'Injection Window',
        message:
            '$bookingRef is at risk of missing the injection window. Please look to inject these bags immediately.',
    },
    Delivery: {
        title: 'Delivery Window',
        message: '$bookingRef is at risk of missing the delivery window. Please contact the customer.',
    },
    MissingBag: {
        title: 'Missing Bag',
        message: '$bookingRef has $missingBagCount bag missing. Please contact relevant partners for more information.',
        message_2:
            '$bookingRef have $missingBagCount bags missing. Please contact relevant partners for more information.',
    },
    CustomerSmsReply: {
        title: 'Customer Message',
        message: '$notificationText',
    },
    FlightCancelled: {
        title: 'Flight Cancelled',
        message: 'Flight $airlineCode-$flightNumber for booking $bookingRef is cancelled. Please action immediately.',
    },
    FlightDelayed: {
        title: 'Flight Delayed',
        message:
            'Flight $airlineCode-$flightNumber is delayed and the bags can no longer be handed over at $currentHandoverSlot. $bookingRef should now be handed over at $updatedHandoverSlot',
    },
    CCDCollection: {
        title: 'CCD carousel collection at risk',
        message:
            '$bookingRef should have been collected from the carousel by $jobCompletionDueTimeText. Please collect the bag(s) ASAP. If there are any issues please take the appropriate action.',
    },
    OneHourCollection: {
        title: 'One Hour Collection',
        message:
            'Booking $bookingRef currently has an ETA that is outside of the booked slot. The driver is expected to arrive between $etaWindow for a $collectionStartTime collection.',
    },
};

const Notification = styled(MenuItem, {
    shouldForwardProp(prop) {
        return prop !== 'bookingtype';
    },
})<INotificationItem>(({ theme, bookingtype }) => ({
    display: 'block',
    padding: '16px',
    marginBottom: '10px',
    borderRadius: '6px',
    cursor: 'default',
    backgroundColor: bookingtype === 'abc' ? theme.palette.alpha.purple['5'] : theme.palette.alpha.error['3'],
    ':hover': {
        backgroundColor: bookingtype === 'abc' ? theme.palette.alpha.purple['15'] : theme.palette.alpha.error['6'],
    },
}));

const NotificationItem: React.FC<IProps> = ({
    notification,
    notificationResolve,
    notificationClose,
    gotoBooking,
    handleActionClose,
}) => {
    const state = useAppSelector((state) => state);
    const navigateTo = useNavigate();
    const userReducer = state.user;
    const loggedinUserRole = userReducer.loggedinUser.userRoles;
    const isExternalAgent = loggedinUserRole.find((role) => role.roleType === 'ExternalAgent');

    const notificationTypeName =
        typeof notification.NotificationType === 'number'
            ? NotificationType[notification.NotificationType]
            : notification.NotificationType;
    const context = getWindowType[notificationTypeName];
    const getNormalizedText = (text: string, params) => {
        let message = text;
        Object.keys(params).forEach((key) => {
            message = message.replace(`$${key}`, params[key]);
        });
        return message;
    };

    let message = context.message.split('$bookingRef');
    if (notificationTypeName === 'MissingBag') {
        const text = Number(notification.NotificationText.missingBagCount) === 1 ? context.message : context.message_2;
        message = getNormalizedText(text, notification.NotificationText).split('$bookingRef');
    }

    if (notificationTypeName === 'CustomerSmsReply') {
        const text = context.message;
        message = getNormalizedText(text, { notificationText: notification.NotificationText.customerReply }).split(
            '$notificationText'
        );
    }

    if (
        notificationTypeName === 'FlightDelayed' ||
        notificationTypeName === 'FlightCancelled' ||
        notificationTypeName === 'CCDCollection' ||
        notificationTypeName === 'OneHourCollection'
    ) {
        const text = context.message;
        message = getNormalizedText(text, notification.NotificationText).split('$bookingRef');
    }

    const bookingRefText =
        notificationTypeName === 'OneHourCollection'
            ? notification.BookingReference
            : notification.BookingJourneyReference;

    const handleNoticationResolve = (notification) => {
        notificationResolve(notification.NotificationId);
        if (isExternalAgent) {
            navigateTo(
                `/customer/bookings/${notification.BookingReference}/${notification.BookingJourneyReference}?tab=message`
            );
            handleActionClose();
        }
    };

    return (
        <Notification bookingtype={notification.ProductType === 1 ? 'abc' : 'ccd'} disableRipple disableTouchRipple>
            <Grid
                container
                spacing={'16px'}
                whiteSpace='pre-wrap'
                sx={{ justifyContent: ['space-between', 'space-between', 'start'] }}>
                <Grid item>
                    <CustomBadge
                        sx={{ fontSize: '10px', lineHeight: '12px', ml: 0 }}
                        purple={notification.ProductType === 1}
                        error={notification.ProductType === 2}>
                        {notification.ProductType === 1 ? 'ABC' : 'CCD'}
                    </CustomBadge>
                </Grid>
                <Grid item sx={{ order: [2, 2, 0], maxWidth: ['100%', '100%', '413px'] }}>
                    <Grid container alignItems='center' justifyContent='space-between'>
                        <Grid item>
                            <Typography sx={{ lineHeight: '24px' }}>
                                {context.title}
                                {notification.NotificationWindow && ` - ${notification.NotificationWindow}`}
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', lineHeight: '16px' }}>
                                {message[0] && message[0] + ' '}
                                {notificationTypeName === 'CustomerSmsReply' && <br />}
                                <Typography
                                    component='span'
                                    onClick={() =>
                                        gotoBooking(notification.BookingReference, notification.BookingJourneyReference)
                                    }
                                    sx={{
                                        color: (t) => t.palette.purple.main,
                                        fontSize: '0.75rem',
                                        lineHeight: '16px',
                                        cursor: 'pointer',
                                    }}>
                                    {bookingRefText}
                                </Typography>{' '}
                                {message[1]}
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item>
                    <IconButton sx={{ p: 0 }} onClick={() => notificationClose(notification.NotificationId)}>
                        <ClearIcon sx={{ fontSize: '16px' }} />
                    </IconButton>
                </Grid>
            </Grid>
            <Grid container justifyContent='space-between' mt='12px' alignItems='center'>
                <Grid item sx={{ pl: [0, 0, '54.8px'] }}>
                    <Typography
                        sx={{
                            fontSize: '0.75rem',
                            lineHeight: '12px',
                            color: (theme) => theme.palette.alpha.primary['40'],
                        }}>
                        {notification.CreatedDateText} | {notification.CreatedTimeText}
                    </Typography>
                </Grid>
                <Grid item container justifyContent='end'>
                    {notificationTypeName === 'CustomerSmsReply' && isExternalAgent && (
                        <Button
                            variant='contained'
                            color='purple'
                            onClick={() => {
                                handleNoticationResolve(notification);
                            }}
                            sx={{ fontSize: '0.75rem', lineHeight: '12px', px: '10px', py: '6px' }}>
                            Resolve
                        </Button>
                    )}
                    {notificationTypeName !== 'CustomerSmsReply' && (
                        <Button
                            variant='contained'
                            color='purple'
                            onClick={() => {
                                handleNoticationResolve(notification);
                            }}
                            sx={{ fontSize: '0.75rem', lineHeight: '12px', px: '10px', py: '6px' }}>
                            Resolve
                        </Button>
                    )}
                </Grid>
            </Grid>
        </Notification>
    );
};

export default NotificationItem;
